import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import {
  BoundingExtent,
  MapGroundPlaneWithFallback,
} from 'features/kestrelLite/MapGroundPlane'
import { ReadOnly3DTilesAnnotations } from 'features/kestrelLite/ReadOnly3DTilesAnnotations'
import { ReadOnly3DTilesInspectionAreas } from 'features/kestrelLite/ReadOnly3DTilesInspectionAreas'
import { TilesLayer } from 'features/kestrelLite/TilesLayer'
import { AxisConfig } from 'features/kestrelLite/utils/annotations'
import React, {
  FC,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box3, Vector3 } from 'three'
import { KVAnnotation, KVInspectionArea } from 'types'

import { AutoFitCamera } from './AutoFitCamera'
import { AxesHelperComponent } from './AxesHelper'
import { FlyToTarget } from './FlyToTarget'
import { KeyboardFlyControls } from './KeyboardFlyControls'
import { MapStyle } from './MapStyleHotbar'
import { MeasurementTool } from './MeasurementTool'
import { OrbitPivotSetter } from './OrbitPivotSetter'

interface LayerState {
  layerName: string
  tilesetPaths: string[]
  status: 'active' | 'loading' | 'fading-out'
  opacity: number
}

export type AreaUnit = 'm' | 'ft'

interface TilesSceneProps {
  kestrelModelId: string
  layers: LayerState[]
  showAxes: boolean
  showAnnotations: boolean
  showLabels: boolean
  showArea: boolean
  areaUnit: AreaUnit
  showInspectionAreas: boolean
  ignoreInspectionAreaVisibility?: boolean
  mapStyle: MapStyle
  origin?: { lat: number; long: number }
  annotations: KVAnnotation[]
  inspectionAreas: KVInspectionArea[]
  inspectionAreaRCIValues?: Map<string, number>
  axisConfig: AxisConfig
  flyToTarget: Vector3 | null
  flyToTargetSize?: { width: number; depth: number }
  flyToTargetTopDownView?: boolean
  flyToTargetAngleOffVertical?: number
  firstRendererRef: MutableRefObject<unknown>
  renderersRef: MutableRefObject<Map<number, unknown>>
  onLayerLoaded: (folder: string) => void
  onLayerRendererReady: (
    renderer: unknown,
    layerIndex: number,
    tilesetIndex: number
  ) => void
  onFlyToComplete: () => void
  setControls: (controls: unknown) => void
  isActive?: boolean
  onMapLoadingChange?: (loading: boolean) => void
  onMapStyleFailed?: (style: 'street' | 'satellite') => void
  isMeasuring?: boolean
  onMeasurementComplete?: () => void
}

/** Offset below the mesh minimum Y to position the map */
const MAP_Y_OFFSET = 5

/**
 * Corrects a bounding box to match the reoriented coordinate system.
 * The tiles use ReorientationPlugin with up: '-y', which inverts Y and Z axes.
 * This function transforms the raw bounding box to match the visual orientation.
 */
function correctBoundingBox(rawBox: Box3): Box3 {
  const corrected = new Box3()
  // X stays the same
  // Y is negated (flip min/max and negate values)
  // Z is negated (flip min/max and negate values)
  corrected.min.set(rawBox.min.x, -rawBox.max.y, -rawBox.max.z)
  corrected.max.set(rawBox.max.x, -rawBox.min.y, -rawBox.min.z)
  return corrected
}

export const TilesScene: FC<TilesSceneProps> = ({
  kestrelModelId,
  layers,
  showAxes,
  showAnnotations,
  showLabels,
  showArea,
  areaUnit,
  showInspectionAreas,
  ignoreInspectionAreaVisibility = false,
  mapStyle,
  origin,
  annotations,
  inspectionAreas,
  inspectionAreaRCIValues,
  axisConfig,
  flyToTarget,
  flyToTargetSize,
  flyToTargetTopDownView,
  flyToTargetAngleOffVertical,
  firstRendererRef,
  renderersRef,
  onLayerLoaded,
  onLayerRendererReady,
  onFlyToComplete,
  setControls,
  isActive = false,
  onMapLoadingChange,
  onMapStyleFailed,
  isMeasuring = false,
  onMeasurementComplete,
}) => {
  const [mapYPosition, setMapYPosition] = useState(-30)
  const [boundingExtent, setBoundingExtent] = useState<BoundingExtent | null>(
    null
  )
  const hasCalculatedBounds = useRef(false)
  const activeLayerName = layers.find((l) => l.status === 'active')?.layerName

  // Reset when active layer changes
  useEffect(() => {
    hasCalculatedBounds.current = false
    setBoundingExtent(null)
  }, [activeLayerName])

  // Compute bounds from L0 tile metadata (available immediately via getBoundingBox)
  const computeBoundsFromMetadata = useCallback(() => {
    if (hasCalculatedBounds.current) return

    const renderers = renderersRef.current
    if (renderers.size === 0) return

    const combinedBox = new Box3()
    let validCount = 0

    renderers.forEach((renderer) => {
      // Renderer type is not fully typed, cast to access getBoundingBox method
      const r = renderer as { getBoundingBox?: (target: Box3) => Box3 }

      if (r.getBoundingBox) {
        const rawBox = new Box3()
        r.getBoundingBox(rawBox)

        if (
          Number.isFinite(rawBox.max.y) &&
          Number.isFinite(rawBox.min.x) &&
          Number.isFinite(rawBox.max.x) &&
          Number.isFinite(rawBox.min.z) &&
          Number.isFinite(rawBox.max.z)
        ) {
          validCount++
          // Apply correction for coordinate system (up: '-y' flips Y and Z)
          const correctedBox = correctBoundingBox(rawBox)
          combinedBox.union(correctedBox)
        }
      }
    })

    // Only finalize when we have bounds from all renderers
    if (validCount === renderers.size && validCount > 0) {
      hasCalculatedBounds.current = true
      setMapYPosition(combinedBox.min.y - MAP_Y_OFFSET)
      setBoundingExtent({
        width: combinedBox.max.x - combinedBox.min.x,
        depth: combinedBox.max.z - combinedBox.min.z,
        centerX: (combinedBox.max.x + combinedBox.min.x) / 2,
        centerZ: (combinedBox.max.z + combinedBox.min.z) / 2,
      })
    }
  }, [renderersRef])

  // Listen for tileset load events to get L0 bounds
  useEffect(() => {
    const renderers = renderersRef.current
    const cleanups: (() => void)[] = []

    renderers.forEach((renderer) => {
      // Renderer type is not fully typed, cast to access event listener methods
      const r = renderer as {
        addEventListener?: (event: string, callback: () => void) => void
        removeEventListener?: (event: string, callback: () => void) => void
      }

      if (r.addEventListener) {
        // load-tile-set fires when tileset.json is parsed (L0 bounds available)
        r.addEventListener('load-tile-set', computeBoundsFromMetadata)

        cleanups.push(() => {
          if (r.removeEventListener) {
            r.removeEventListener('load-tile-set', computeBoundsFromMetadata)
          }
        })
      }
    })

    // Try immediately in case tileset already loaded
    computeBoundsFromMetadata()

    return () => cleanups.forEach((cleanup) => cleanup())
  }, [
    renderersRef,
    renderersRef.current.size,
    activeLayerName,
    computeBoundsFromMetadata,
  ])

  return (
    <>
      <PerspectiveCamera
        makeDefault
        fov={60}
        far={100000}
        near={0.1}
        position={[0, 50, 100]}
      />

      {showAxes && <AxesHelperComponent size={100} />}

      {layers.map((layer, index) => (
        <TilesLayer
          key={layer.layerName}
          kestrelModelId={kestrelModelId}
          tilesetPaths={layer.tilesetPaths}
          visible={true}
          opacity={layer.opacity}
          onLoaded={() => onLayerLoaded(layer.layerName)}
          onRendererReady={(renderer, tilesetIndex) =>
            onLayerRendererReady(renderer, index, tilesetIndex)
          }
        />
      ))}

      <AutoFitCamera tilesRenderer={firstRendererRef.current} />

      {mapStyle !== 'off' && origin && (
        <MapGroundPlaneWithFallback
          lat={origin.lat}
          long={origin.long}
          metersPerUnit={1}
          mapStyle={mapStyle}
          yPosition={mapYPosition}
          boundingExtent={boundingExtent ?? undefined}
          onLoadingChange={onMapLoadingChange}
          onStyleFailed={onMapStyleFailed}
          kestrelModelId={kestrelModelId}
        />
      )}

      <ambientLight intensity={1.0} />
      <directionalLight position={[50, 100, 50]} intensity={1.5} />
      <directionalLight position={[-50, 50, -50]} intensity={0.5} />

      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.1}
        minDistance={0.1}
        maxDistance={10000}
        enabled={!isMeasuring}
        ref={(ref) => {
          if (ref) setControls(ref)
        }}
      />

      <OrbitPivotSetter />
      <KeyboardFlyControls speed={2} isActive={isActive && !isMeasuring} />

      <MeasurementTool
        isActive={isMeasuring}
        onMeasurementComplete={onMeasurementComplete}
      />

      {showInspectionAreas && (
        <ReadOnly3DTilesInspectionAreas
          inspectionAreas={inspectionAreas}
          axisConfig={axisConfig}
          showLabels={true}
          showArea={showArea}
          areaUnit={areaUnit}
          rciValues={inspectionAreaRCIValues}
          ignoreVisibility={ignoreInspectionAreaVisibility}
        />
      )}

      {showAnnotations && (
        <ReadOnly3DTilesAnnotations
          annotations={annotations}
          axisConfig={axisConfig}
          showLabels={showLabels}
          showArea={showArea}
          areaUnit={areaUnit}
        />
      )}

      <FlyToTarget
        target={flyToTarget}
        targetSize={flyToTargetSize}
        topDownView={flyToTargetTopDownView}
        angleOffVerticalDegrees={flyToTargetAngleOffVertical}
        onComplete={onFlyToComplete}
      />
    </>
  )
}
